/**
 * Litterly Image Section JavaScript
 * Handles parallax effects, countdown timers, and other interactive features
 */

(() => {
if (window.__litterlyImageScriptLoaded) return;
window.__litterlyImageScriptLoaded = true;

class LitterlyImageSection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.imageWrapper = section.querySelector('.litterly-image__image-wrapper');
    this.countdownElements = section.querySelectorAll('[data-date]');
    this.cleanupCallbacks = [];
    this.countdownTimers = [];
    this.observer = null;
    
    this.init();
  }

  init() {
    this.initParallax();
    this.initCountdown();
    this.initScrollAnimations();
  }

  /**
   * Initialize parallax effect for images
   */
  initParallax() {
    const parallaxContainers = this.section.querySelectorAll('[data-parallax]');
    
    if (parallaxContainers.length === 0) return;

    const handleParallax = () => {
      const scrollTop = window.pageYOffset;
      const windowHeight = window.innerHeight;
      
      parallaxContainers.forEach(container => {
        const speed = parseFloat(container.dataset.parallax) || 0.5;
        const rect = container.getBoundingClientRect();
        const elementTop = rect.top + scrollTop;
        const elementHeight = rect.height;
        
        // Only apply parallax when element is in viewport
        if (elementTop < scrollTop + windowHeight && elementTop + elementHeight > scrollTop) {
          const yPos = -(scrollTop - elementTop) * speed;
          const image = container.querySelector('.litterly-image__image--parallax');
          
          if (image) {
            image.style.transform = `translateY(${yPos}px)`;
          }
        }
      });
    };

    // Throttle scroll events for performance
    let ticking = false;
    const requestTick = () => {
      if (!ticking) {
        window.requestAnimationFrame(handleParallax);
        ticking = true;
        setTimeout(() => { ticking = false; }, 16); // ~60fps
      }
    };

    window.addEventListener('scroll', requestTick);
    window.addEventListener('resize', requestTick);
    this.cleanupCallbacks.push(() => {
      window.removeEventListener('scroll', requestTick);
      window.removeEventListener('resize', requestTick);
    });
    
    // Initial call
    handleParallax();
  }

  /**
   * Initialize countdown timers
   */
  initCountdown() {
    this.countdownElements.forEach(countdown => {
      const targetDate = new Date(countdown.dataset.date).getTime();
      
      if (isNaN(targetDate)) {
        console.warn(`Invalid date format for countdown: ${countdown.dataset.date}`);
        return;
      }

      const updateCountdown = () => {
        const now = new Date().getTime();
        const distance = targetDate - now;

        if (distance < 0) {
          // Countdown expired
          this.updateCountdownDisplay(countdown, 0, 0, 0, 0);
          return;
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        this.updateCountdownDisplay(countdown, days, hours, minutes, seconds);
      };

      // Update immediately and then every second
      updateCountdown();
      this.countdownTimers.push(setInterval(updateCountdown, 1000));
    });
  }

  /**
   * Update countdown display
   */
  updateCountdownDisplay(countdown, days, hours, minutes, seconds) {
    const daysElement = countdown.querySelector('.litterly-image__countdown-days');
    const hoursElement = countdown.querySelector('.litterly-image__countdown-hours');
    const minutesElement = countdown.querySelector('.litterly-image__countdown-minutes');
    const secondsElement = countdown.querySelector('.litterly-image__countdown-seconds');

    if (daysElement) daysElement.textContent = this.padNumber(days);
    if (hoursElement) hoursElement.textContent = this.padNumber(hours);
    if (minutesElement) minutesElement.textContent = this.padNumber(minutes);
    if (secondsElement) secondsElement.textContent = this.padNumber(seconds);
  }

  /**
   * Pad single digit numbers with leading zero
   */
  padNumber(num) {
    return num.toString().padStart(2, '0');
  }

  /**
   * Initialize scroll animations
   */
  initScrollAnimations() {
    // Check if IntersectionObserver is supported
    if (!window.IntersectionObserver) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('litterly-image--in-view');
          this.observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe the section
    this.observer.observe(this.section);
  }

  /**
   * Cleanup event listeners
   */
  destroy() {
    this.cleanupCallbacks.forEach(callback => callback());
    this.cleanupCallbacks = [];

    this.countdownTimers.forEach(timer => clearInterval(timer));
    this.countdownTimers = [];

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.section) {
      this.section.litterlyImage = null;
    }
  }
}

function initLitterlyImageSection(section) {
  if (section.litterlyImage) {
    section.litterlyImage.destroy();
  }

  section.litterlyImage = new LitterlyImageSection(section);
}

/**
 * Initialize Litterly Image sections when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
  const litterlyImageSections = document.querySelectorAll('[data-section-type="litterly-image"]');
  
  litterlyImageSections.forEach(section => {
    initLitterlyImageSection(section);
  });
});

/**
 * Handle Shopify theme editor events
 */
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-section-type="litterly-image"]');
    if (section) {
      initLitterlyImageSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-section-type="litterly-image"]');
    if (section && section.litterlyImage) {
      section.litterlyImage.destroy();
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    const section = event.target.querySelector('[data-section-type="litterly-image"]');
    if (section) {
      // Refresh animations when section is selected in editor
      section.classList.remove('litterly-image--in-view');
      setTimeout(() => {
        section.classList.add('litterly-image--in-view');
      }, 100);
    }
  });
}

/**
 * Export for potential module usage
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = LitterlyImageSection;
}

window.LitterlyImageSection = LitterlyImageSection;
})();
